import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, type Ref, type ComputedRef } from 'vue'
import { track, Events, BrazeEvents, GtmEvents } from '@outputinc/o-analytics'
import gtmClient from '@/utils/gtmClient'
import { type Screen, ScreenName, HotkeyElement, ScreenSetId } from '@/types'
import { useProductStore } from '@/store/ProductStore'
import { useCheckoutStore } from '@/store/CheckoutStore'
import { useUserStore } from './UserStore'
import { useErrorsStore } from './ErrorsStore'
import screenSets from '@/store/screens'
import { useRouter } from 'vue-router'
import { useBrazeStore } from './BrazeStore'
import { usePlanStore } from './PlanStore'

const CONFIRMATION_SCREENS = new Set([ScreenName.Confirmation, ScreenName.NoPayPlanConfirmation, ScreenName.UpgradeConfirmation, ScreenName.OfferConfirmation])

const TRIAL_TIMELINE_SEEN_KEY = 'trial_timeline_seen'

export const useScreenStore = defineStore('ScreenStore', () => {
  const checkoutStore = useCheckoutStore()
  const productStore = useProductStore()
  const userStore = useUserStore()
  const planStore = usePlanStore()
  const { clearAppError } = useErrorsStore()
  const { selectedPlanProduct } = storeToRefs(planStore)
  const { setCompletedScreen: setCheckoutScreenComplete } = checkoutStore
  const { trackEmailUser } = useBrazeStore()
  const router = useRouter()

  const { productName, productId } = storeToRefs(productStore)
  const { outputUid } = storeToRefs(userStore)

  const screens: Ref<Screen[]> = ref([])
  const screenSet = ref()

  // Hydrated from sessionStorage so a reload doesn't bounce checkout back to the trial timeline
  const trialTimelineSeen = ref(!!sessionStorage.getItem(TRIAL_TIMELINE_SEEN_KEY))

  const screenIndex = ref(0)

  const confirmationScreenName = computed(() => `Welcome to ${productName.value}`)

  const busy = ref(false)
  const canNavigateNext = ref(true)

  const nextScreen: ComputedRef<Screen> = computed(() => screens.value[screenIndex.value + 1])
  const previousScreen: ComputedRef<Screen | null> = computed(() => screens.value[screenIndex.value - 1])
  const currentScreen: ComputedRef<Screen> = computed(() => screens.value[screenIndex.value])
  const currentScreenCompleted = computed(() => currentScreen.value.completed)
  const currentScreenId = computed(() => currentScreen.value?.id)
  const isConfirmationScreen = computed(() => CONFIRMATION_SCREENS.has(currentScreenId.value))
  const currentScreenName = computed(() => (isConfirmationScreen.value ? confirmationScreenName : currentScreen.value?.name))
  const currentScreenProduct = computed(() => selectedPlanProduct.value || productId.value)

  const setBusy = (isBusy: boolean) => (busy.value = isBusy)
  const setCanNavigateNext = (canNavigate: boolean) => (canNavigateNext.value = canNavigate)

  const setScreens = (screenSetId: ScreenSetId) => {
    if (screenSet.value === screenSetId) {
      return
    }

    screens.value = []

    const screenSetFromId = screenSets.find(set => set.id === screenSetId)

    screenSet.value = screenSetId

    addScreens([...(screenSetFromId?.screens ? [...screenSetFromId.screens] : [])])
  }

  const restoreScreens = () => {
    const { completedScreens } = storeToRefs(checkoutStore)

    screens.value = screens.value.map((screen: Screen) => {
      const completedScreen = completedScreens.value.find((checkoutScreen: Screen) => {
        return screen.id === checkoutScreen.id && screen.params?.surveyId === checkoutScreen.params?.surveyId
      })

      // Trial timeline completion is also persisted in sessionStorage, so a reload doesn't send the user back to it
      if (screen.id === ScreenName.TrialTimeline && trialTimelineSeen.value) {
        return { ...screen, ...completedScreen, completed: true }
      }

      return completedScreen ? { ...screen, ...completedScreen } : screen
    })

    if (firstIncompleteScreen.value) {
      setCurrentScreen(firstIncompleteScreen.value)
    }
  }

  const addScreens = (newScreens: Screen[]) => {
    const notAdded = newScreens.filter(
      newScreen => !screens.value.find(existing => existing.id === newScreen.id && existing.params?.surveyId === newScreen.params?.surveyId)
    )

    const existingScreen = notAdded.find(screen => screen.id === ScreenName.Existing)

    if (existingScreen) {
      screens.value = [existingScreen]
      return
    }
    screens.value = [...notAdded, ...screens.value]
  }

  const setCurrentScreen = (screen: Screen) => {
    screenIndex.value = screens.value.indexOf(screen)
  }

  // Trial timeline is part of the checkout flow only when the plan has a trial. Composing it in/out
  // here — rather than always including it and redirecting past it — keeps the screen set in sync with
  // the route, so the header and step pointer can't desync for no-trial plans (WEB-7670).
  const setTrialTimelineVisible = (visible: boolean) => {
    const present = screens.value.some(screen => screen.id === ScreenName.TrialTimeline)

    if (visible && !present) {
      const paymentIndex = screens.value.findIndex(screen => screen.id === ScreenName.PaymentMethod)
      const insertAt = paymentIndex === -1 ? screens.value.length : paymentIndex

      screens.value = [...screens.value.slice(0, insertAt), { id: ScreenName.TrialTimeline, name: 'How your trial works' }, ...screens.value.slice(insertAt)]
    } else if (!visible && present) {
      screens.value = screens.value.filter(screen => screen.id !== ScreenName.TrialTimeline)
    }
  }

  const setTrialTimelineSeen = () => {
    trialTimelineSeen.value = true
    sessionStorage.setItem(TRIAL_TIMELINE_SEEN_KEY, '1')
  }

  const clearTrialTimelineSeen = () => {
    trialTimelineSeen.value = false
    sessionStorage.removeItem(TRIAL_TIMELINE_SEEN_KEY)
  }

  // Clears session-scoped screen state (e.g. on logout)
  const resetScreenSessionState = () => {
    clearTrialTimelineSeen()
  }

  const setScreenComplete = (completed: boolean) => {
    currentScreen.value.completed = completed

    if (completed && currentScreen.value.id === ScreenName.TrialTimeline) {
      setTrialTimelineSeen()
    }

    setCheckoutScreenComplete(currentScreen.value)

    sendScreenCompleteEvents()
  }

  const hasPlanSelection = computed(() => !!screens.value.some((screen: Screen) => screen.id === ScreenName.PlanSelection))

  const hotKeys: Ref<string[]> = ref([])

  const firstIncompleteScreen = computed(() => {
    const incomplete = screens.value.find((screen: Screen) => {
      return screen.completed === false || !screen.completed
    })

    return incomplete
  })

  const sendRemoteScreenUpdate = (screen: Screen): void => {
    const { id: name, ...route } = screen

    const detail = {
      ...route,
      name
    }

    const evt = new CustomEvent('remoteScreenUpdate', { detail })
    document.dispatchEvent(evt)

    setCurrentScreen(screen)
  }

  const navigateTo = (screen: Screen) => {
    clearAppError()

    if (!router) {
      sendRemoteScreenUpdate(screen)

      return
    }

    const { query, params: currentParams } = router.currentRoute.value

    const { id: name, params } = screen

    router
      .push({
        name,
        query,
        params: {
          ...currentParams,
          ...params
        }
      })
      .then(routerResponse => {
        if (!routerResponse) {
          setCurrentScreen(screen)
        }
      })
  }

  const navigateNext = () => {
    if (nextScreen.value && currentScreenCompleted.value) {
      navigateTo(nextScreen.value)
    }

    setBusy(false)
  }

  const navigatePrevious = () => {
    const { isComplete } = storeToRefs(checkoutStore)

    if (previousScreen.value && !isComplete.value) {
      navigateTo(previousScreen.value)
    }

    setBusy(false)
  }

  const setHotKeys = () => {
    const currentScreenElement = document.getElementById(`screen-${currentScreen.value.id}`)

    if (currentScreenElement) {
      const hotKeyElements = currentScreenElement.querySelectorAll<HotkeyElement>('[data-hotkey]')

      hotKeys.value = Array.from(hotKeyElements).map(key => key.dataset.hotkey)
    }
  }

  const sendScreenCompleteEvents = () => {
    const { track: gtmTrackEvent } = gtmClient

    gtmTrackEvent(GtmEvents.CHECKOUT_SUBSCRIPTION_STEP_COMPLETED, {
      arcadeStepCompleted: currentScreenId.value,
      products: [currentScreenProduct.value]
    })

    trackEmailUser({
      externalId: outputUid.value,
      name: BrazeEvents.CHECKOUT_SUBSCRIPTION_SCREEN_COMPLETE,
      time: new Date().toISOString(),
      properties: {
        step: currentScreenId.value,
        products: currentScreenProduct.value
      }
    })

    track(Events.CHECKOUT_SUBSCRIPTION_SCREEN_COMPLETE, {
      products: [currentScreenProduct.value],
      ...currentScreen.value
    })
  }

  const progress = computed(() => Math.floor(((screenIndex.value + 1) / screens.value.length) * 100))

  return {
    addScreens,
    busy,
    canNavigateNext,
    resetScreenSessionState,
    trialTimelineSeen,
    currentScreen,
    currentScreenCompleted,
    currentScreenId,
    currentScreenName,
    currentScreenProduct,
    firstIncompleteScreen,
    hasPlanSelection,
    isConfirmationScreen,
    hotKeys,
    navigateNext,
    navigatePrevious,
    navigateTo,
    nextScreen,
    previousScreen,
    progress,
    restoreScreens,
    screenIndex,
    screens,
    setBusy,
    setCanNavigateNext,
    sendRemoteScreenUpdate,
    setCurrentScreen,
    setScreenComplete,
    setTrialTimelineVisible,
    setHotKeys,
    setScreens
  }
})
